// Graph-backed capabilities surface (step 6, "what to build"). Shows the AI's
// proposed capabilities — each addressing one or more synthesised needs — and
// lets the PM curate: edit a title, reject, or promote a candidate into a
// `capability` graph node linked to the needs it addresses (a challenge it
// mitigates / an opportunity it enables; the edge type is derived server-side).
// Generation streams from the Function URL; the proposal is read from the
// capabilities API. Propose-then-promote: only promoted candidates hit the graph.
// Also shows the existing capability nodes (read from the live graph) so the
// view is self-sufficient on return visits.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Sparkles, Check, Trash2, Wrench, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useProject, useUpdateProject } from '@/hooks/use-projects';
import { useCanvasData } from '@/hooks/use-canvas-data';
import {
  useCapabilities,
  useGenerateCapabilities,
  usePromoteCapability,
  useDismissCapability,
} from '@/hooks/use-capabilities';
import { toast } from '@/hooks/use-toast';
import { pendingProposalFriction } from '@/lib/generation-friction';
import { WORKSPACE_WIDTH } from '@/lib/workspace-width';
import { StepHeader } from '@/features/project/StepHeader';
import { PrerequisiteHint } from '@/features/project/PrerequisiteHint';
import { useMissingPrerequisite } from '@/features/project/use-missing-prerequisite';
import type { CapabilityCandidate, PromoteCapabilityInput } from '@/types/api';

// The need a capability addresses, resolved from the live graph for display.
interface AddressedNeed {
  id: string;
  title: string;
  type: string; // 'challenge' | 'opportunity' (other types shouldn't appear)
  verb: string; // 'mitigates' | 'enables'
}

interface CapabilitiesViewProps {
  projectId: string;
}

export function CapabilitiesView({ projectId }: CapabilitiesViewProps) {
  const { data: project } = useProject(projectId);
  const { data: proposal } = useCapabilities(projectId);
  const { data: canvas } = useCanvasData(projectId);
  const generate = useGenerateCapabilities();
  const promote = usePromoteCapability(projectId);
  const dismiss = useDismissCapability(projectId);
  const updateProject = useUpdateProject();

  const reviewed = !!project?.capabilitiesReviewed;
  // A step id when there are no synthesised needs to address, null when
  // usable, undefined while the graph is still loading.
  const missing = useMissingPrerequisite(projectId, 'capabilities');
  const hinted = typeof missing === 'string';
  // Rows collapse to their headline by default; the broadcast below lets the
  // list header expand/collapse every row without lifting per-row state.
  const [expandAll, setExpandAll] = useState(false);
  const [expandSeq, setExpandSeq] = useState(0);

  // Resolve addressed-need ids → {title, type, verb} from the live graph.
  const needById = useMemo(() => {
    const m = new Map<string, { title: string; type: string }>();
    for (const n of canvas?.nodes ?? []) {
      m.set(n.id, { title: (n.data as { label?: string } | undefined)?.label || 'Untitled', type: n.type || '' });
    }
    return m;
  }, [canvas?.nodes]);

  const resolveNeeds = (ids: string[]): AddressedNeed[] =>
    ids
      .map((id) => {
        const n = needById.get(id);
        if (!n) return null;
        return { id, title: n.title, type: n.type, verb: n.type === 'challenge' ? 'mitigates' : 'enables' };
      })
      .filter((n): n is AddressedNeed => n !== null);

  const proposed = useMemo(
    () => (proposal?.candidates ?? []).filter((c) => c.status === 'proposed'),
    [proposal?.candidates],
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const friction = pendingProposalFriction(proposed.length);
  // Existing capability nodes, read from the live graph rather than the
  // proposal — covers promotions from earlier runs and manually-drawn
  // capabilities alike. Needs are the mitigates/enables edges each one carries.
  const existingCapabilities = useMemo(() => {
    const needIdsByCap = new Map<string, string[]>(); // capabilityId -> addressed need ids
    for (const e of canvas?.edges ?? []) {
      const edgeType = (e.data as { edgeType?: string } | undefined)?.edgeType;
      if (edgeType !== 'mitigates' && edgeType !== 'enables') continue;
      const ids = needIdsByCap.get(e.source);
      if (ids) ids.push(e.target);
      else needIdsByCap.set(e.source, [e.target]);
    }
    return (canvas?.nodes ?? [])
      .filter((n) => n.type === 'capability')
      .map((n) => ({
        id: n.id,
        title: (n.data as { label?: string } | undefined)?.label || 'Untitled',
        needIds: needIdsByCap.get(n.id) ?? [],
      }));
  }, [canvas?.nodes, canvas?.edges]);

  const capabilitySummary = useMemo(() => {
    const needCount = new Set(existingCapabilities.flatMap((c) => c.needIds)).size;
    const total = `${existingCapabilities.length} capabilit${existingCapabilities.length === 1 ? 'y' : 'ies'}`;
    return needCount > 0 ? `${total} addressing ${needCount} need${needCount === 1 ? '' : 's'}` : total;
  }, [existingCapabilities]);

  const { mutateAsync: generateCapabilities } = generate;
  const handleGenerate = useCallback(async () => {
    try {
      const candidates = await generateCapabilities(projectId);
      toast({ title: candidates.length ? `Found ${candidates.length} capabilit${candidates.length === 1 ? 'y' : 'ies'}` : 'No capabilities found' });
    } catch (err) {
      toast({ title: 'Capability generation failed', description: err instanceof Error ? err.message : '', variant: 'destructive' });
    }
  }, [generateCapabilities, projectId]);


  const setReviewed = async (value: boolean) => {
    try {
      await updateProject.mutateAsync({ projectId, patch: { capabilitiesReviewed: value } });
    } catch (err) {
      toast({ title: 'Save failed', description: err instanceof Error ? err.message : '', variant: 'destructive' });
    }
  };

  // Disabling promote/dismiss while a mutation is in flight is also the agreed
  // near-term guard against the curation read-modify-write race.
  const busy = promote.isPending || dismiss.isPending;

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className={`${WORKSPACE_WIDTH} mx-auto space-y-6`}>
        <StepHeader
          projectId={projectId}
          stepId="capabilities"
          actions={
            <div className="flex items-center gap-2 shrink-0">
              {!hinted && (
                <Button
                  variant={reviewed ? 'outline' : 'brand'}
                  onClick={() => (friction === null ? handleGenerate() : setConfirmOpen(true))}
                  disabled={generate.isPending}
                >
                  {generate.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  {proposal ? 'Re-generate' : 'Suggest capabilities'}
                </Button>
              )}
              {reviewed ? (
                <Button variant="outline" onClick={() => setReviewed(false)} disabled={updateProject.isPending}>
                  Re-open step
                </Button>
              ) : proposal ? (
                <Button variant="outline" onClick={() => setReviewed(true)} disabled={updateProject.isPending}>
                  <Check className="h-4 w-4 mr-2" />
                  Mark step complete
                </Button>
              ) : null}
            </div>
          }
        />

        {generate.isPending ? (
          // Streamed previews replace the (stale) proposal list while a run is
          // in flight: read-only rows pop in as each capability's JSON completes.
          <div className="rounded-lg border bg-card p-4 space-y-3" aria-live="polite">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {generate.streamedCandidates.length > 0
                ? `Drafting — ${generate.streamedCandidates.length} capabilit${generate.streamedCandidates.length === 1 ? 'y' : 'ies'} so far`
                : generate.streamStarted
                ? 'Drafting the first capability…'
                : 'Drafting capabilities for your synthesised needs…'}
            </div>
            {(generate.streamedCandidates.length > 0 || generate.draftingTitle) && (
              <ul className="space-y-1.5">
                {generate.streamedCandidates.map((c, i) =>
                  c.title ? (
                    <li key={i} className="rounded-md border bg-card px-3 py-2 animate-in fade-in slide-in-from-bottom-1">
                      <p className="min-w-0 truncate text-sm font-medium">{c.title}</p>
                      {(() => {
                        const addressed = resolveNeeds(c.addressedNodeIds ?? []);
                        return addressed.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5 pt-1">
                            {addressed.map((n) => (
                              <Badge key={n.id} tone={n.type as BadgeTone} title={`${n.verb} this ${n.type}`}>
                                <span className="font-medium uppercase tracking-wide">{n.verb}</span> {n.title}
                              </Badge>
                            ))}
                          </div>
                        ) : null;
                      })()}
                    </li>
                  ) : null,
                )}
                {generate.draftingTitle && (
                  <li className="flex items-center gap-2 rounded-md border border-dashed bg-card px-3 py-2 opacity-70 animate-pulse">
                    <span className="min-w-0 flex-1 truncate text-sm">{generate.draftingTitle}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">drafting…</span>
                  </li>
                )}
              </ul>
            )}
          </div>
        ) : !proposal ? (
          missing ? (
            // No synthesised needs to address yet: point at step 5 instead of
            // offering a Suggest that would 400. Nested inside the empty state
            // so an existing proposal keeps rendering if the needs are later deleted.
            <PrerequisiteHint stepId="capabilities" missing={missing} />
          ) : (
            <div className="rounded-lg border bg-card p-12 text-center">
              <Wrench className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <h2 className="font-semibold">No capabilities yet</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Capabilities address the themes you promoted in step 5. Promote some there first, then
                generate.
              </p>
            </div>
          )
        ) : proposed.length === 0 ? (
          <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
            No capabilities left to review. Re-generate to refresh.
          </div>
        ) : (
          <>
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-muted-foreground"
                onClick={() => {
                  setExpandAll(!expandAll);
                  setExpandSeq((v) => v + 1);
                }}
              >
                {expandAll ? 'Collapse all' : 'Expand all'}
              </Button>
            </div>
          <ul className="space-y-2">
            {proposed.map((c) => (
              <CandidateRow
                key={c.id}
                candidate={c}
                needs={resolveNeeds(c.addressedNodeIds)}
                expandSignal={expandSeq}
                expandTo={expandAll}
                onPromote={(payload) =>
                  promote
                    .mutateAsync(payload)
                    .then(() => toast({ title: 'Promoted to a capability node' }))
                    .catch((err) => toast({ title: 'Promote failed', description: err instanceof Error ? err.message : '', variant: 'destructive' }))
                }
                onReject={() =>
                  dismiss
                    .mutateAsync(c.id)
                    .catch((err) => toast({ title: 'Reject failed', description: err instanceof Error ? err.message : '', variant: 'destructive' }))
                }
                busy={busy}
              />
            ))}
          </ul>
          </>
        )}

        {existingCapabilities.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Wrench className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold">Already on your graph</h2>
            </div>
            <p className="text-xs text-muted-foreground">{capabilitySummary}</p>
            <ul className="space-y-1.5">
              {existingCapabilities.map((c) => {
                const addressed = resolveNeeds(c.needIds);
                return (
                  <li key={c.id} className="rounded-md border bg-card px-3 py-2">
                    <p className="min-w-0 truncate text-sm font-medium">{c.title}</p>
                    {addressed.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {addressed.map((n) => (
                          <Badge key={n.id} tone={n.type as BadgeTone} title={`${n.verb} this ${n.type}`}>
                            <span className="font-medium uppercase tracking-wide">{n.verb}</span> {n.title}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title="Replace the proposals you haven't reviewed?"
        body={`You have ${friction ?? 0} proposed capabilit${friction === 1 ? 'y' : 'ies'} waiting for review. Re-generating replaces them.`}
        confirmLabel="Re-generate"
        onConfirm={() => {
          setConfirmOpen(false);
          void handleGenerate();
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}

function CandidateRow({
  candidate,
  needs,
  onPromote,
  onReject,
  busy,
  expandSignal,
  expandTo,
}: {
  candidate: CapabilityCandidate;
  needs: AddressedNeed[];
  onPromote: (payload: PromoteCapabilityInput) => void;
  onReject: () => void;
  busy: boolean;
  expandSignal: number;
  expandTo: boolean;
}) {
  const [title, setTitle] = useState(candidate.title);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (expandSignal > 0) setExpanded(expandTo);
  }, [expandSignal, expandTo]);

  return (
    <li className="rounded-md border bg-card shadow-xs p-3 space-y-2">
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="mt-1.5 shrink-0 text-muted-foreground"
          aria-label={expanded ? 'Collapse details' : 'Expand details'}
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <div className="min-w-0 flex-1 space-y-1.5">
          <input
            type="text"
            value={title}
            aria-label="Capability title"
            onChange={(e) => setTitle(e.target.value)}
            maxLength={2000}
            className="w-full bg-transparent font-medium text-sm leading-snug focus:outline-hidden focus:ring-2 focus:ring-ring rounded px-1 -mx-1"
          />
          {expanded && candidate.description && <p className="text-xs text-muted-foreground">{candidate.description}</p>}
          {expanded && candidate.rationale && <p className="text-xs text-muted-foreground/80 italic">{candidate.rationale}</p>}
          {needs.length === 0 ? (
            <span className="block text-xs text-muted-foreground/70">No linked needs remain — reject or re-generate.</span>
          ) : expanded ? (
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {needs.map((n) => (
                <Badge key={n.id} tone={n.type as BadgeTone} title={`${n.verb} this ${n.type}`}>
                  <span className="font-medium uppercase tracking-wide">{n.verb}</span> {n.title}
                </Badge>
              ))}
            </div>
          ) : (
            <span className="block text-xs text-muted-foreground">
              addresses {needs.length} need{needs.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            size="sm"
            onClick={() =>
              onPromote({
                candidateId: candidate.id,
                title: title.trim() || candidate.title,
                description: candidate.description,
                // Send only the needs that still resolve to a live node (what the
                // row shows) rather than the original generation-time ids — some
                // may have been deleted since. The backend re-validates too.
                addressedNodeIds: needs.map((n) => n.id),
              })
            }
            disabled={busy || !title.trim() || needs.length === 0}
          >
            <Wrench className="h-3.5 w-3.5 mr-1.5" />
            Promote
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={onReject}
            disabled={busy}
            aria-label="Reject capability"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </li>
  );
}
